/**
 * Who may do what. Two decisions live here and every request goes through them:
 *
 *   authorize      may this caller reach this route at all. Each route in `routes.ts` declares the
 *                  access it needs, and the Worker refuses before any handler runs.
 *   canReadPatient may this caller see this patient's clinical record. Routes say what kind of
 *                  caller someone is, not which patients they may see, so clinical handlers ask
 *                  this as well (PRI-003).
 *
 * Both are pure, so every allowed path and every refusal is tested without a database.
 *
 * Platform roles are held by Lattice's own people and stored in Better Auth's `user.role` column as a
 * comma-separated list, so one person can hold two (a physician who is also an admin). Each role
 * grants the permissions listed in PLATFORM_PERMISSIONS and nothing else. Clinic roles are held per
 * clinic in Better Auth's `member` table, also comma-separated, so a solo practitioner is both the
 * clinic's owner and its practitioner. They grant CLINIC_PERMISSIONS in one clinic at a time: the
 * session's active clinic. A member of two clinics works in whichever they chose, and a request
 * never mixes them.
 *
 * A formula is approved only by a physician (CLI-006). Super admins and admins run the business but
 * do not sign scripts, so a script always names someone who holds `physician`.
 */

export const PLATFORM_ROLES = [
  "super_admin",
  "admin",
  "physician",
  "operations",
  "support",
  "formulation",
] as const;

export type PlatformRole = (typeof PLATFORM_ROLES)[number];

export function isPlatformRole(role: string): role is PlatformRole {
  return PLATFORM_ROLES.some((known) => known === role);
}

/**
 * The platform roles in a stored `user.role` value. Better Auth writes "user" for someone with no
 * platform role, and an unknown name is dropped rather than trusted.
 */
export function parsePlatformRoles(stored: string | null): readonly PlatformRole[] {
  return (stored ?? "")
    .split(",")
    .map((part) => part.trim())
    .filter(isPlatformRole);
}

export const PLATFORM_PERMISSIONS_LIST = [
  "user.list",
  "user.get",
  "user.ban",
  "user.set_role",
  "patient.read_record",
  "review.list",
  "review.read",
  "review.decide",
  "reaction.read",
  "reaction.resolve",
  "organization.read",
  "organization.move_link",
  "organization.verify_licence",
  "job.read",
  "job.retry",
  "pharmacy.read",
  "pharmacy.update",
  "billing.read",
  "billing.update",
  "billing.refund",
  "engine.read",
  "engine.edit",
  "engine.evaluate",
  "engine.rate",
  "engine.promote",
  "funnel.read",
  "audit.read",
] as const;

export type PlatformPermission = (typeof PLATFORM_PERMISSIONS_LIST)[number];

const OPERATIONAL: readonly PlatformPermission[] = [
  "user.list",
  "user.get",
  "user.ban",
  "user.set_role",
  "patient.read_record",
  "review.list",
  "reaction.read",
  "organization.read",
  "organization.move_link",
  "organization.verify_licence",
  "job.read",
  "job.retry",
  "pharmacy.read",
  "pharmacy.update",
  "billing.read",
  "billing.update",
  "billing.refund",
  "engine.read",
  "funnel.read",
  "audit.read",
];

/*
  Operations has no identified record and no reaction list: a pharmacy order already carries what
  fulfilment needs. The audit log lists who read whose record, so only super admins and admins read
  it. Formulation staff work on de-identified evaluation cases and reach no clinical route.
*/
export const PLATFORM_PERMISSIONS: Readonly<Record<PlatformRole, readonly PlatformPermission[]>> = {
  super_admin: [...OPERATIONAL, "engine.edit", "engine.evaluate", "engine.rate", "engine.promote"],
  admin: OPERATIONAL,
  physician: [
    "user.list",
    "user.get",
    "patient.read_record",
    "review.list",
    "review.read",
    "review.decide",
    "reaction.read",
    "reaction.resolve",
    "job.read",
  ],
  operations: [
    "user.list",
    "user.get",
    "organization.read",
    "job.read",
    "job.retry",
    "pharmacy.read",
    "pharmacy.update",
    "billing.read",
    "billing.update",
  ],
  support: [
    "user.list",
    "user.get",
    "user.ban",
    "patient.read_record",
    "reaction.read",
    "organization.read",
    "organization.move_link",
    "job.read",
    "job.retry",
    "pharmacy.read",
    "billing.read",
    "billing.update",
    "billing.refund",
    "funnel.read",
  ],
  formulation: ["engine.read", "engine.edit", "engine.evaluate", "engine.rate", "engine.promote"],
};

export const CLINIC_ROLES = ["owner", "practitioner", "assistant"] as const;

export type ClinicRole = (typeof CLINIC_ROLES)[number];

export function isClinicRole(value: string): value is ClinicRole {
  return CLINIC_ROLES.some((role) => role === value);
}

/**
 * The clinic roles in a stored `member.role` or `invitation.role` value, in CLINIC_ROLES order with
 * duplicates dropped. A name that is not a clinic role is skipped rather than refused, so a role
 * retired from code leaves its holder with the roles that remain.
 */
export function parseClinicRoles(stored: string | null): readonly ClinicRole[] {
  const names = new Set((stored ?? "").split(",").map((name) => name.trim()));

  return CLINIC_ROLES.filter((role) => names.has(role));
}

/** The stored form of a set of clinic roles, as Better Auth's organization plugin writes it. */
export function clinicRolesValue(roles: readonly ClinicRole[]): string {
  return CLINIC_ROLES.filter((role) => roles.includes(role)).join(",");
}

export type ClinicPermission =
  | "member.invite"
  | "member.update"
  | "member.remove"
  | "licence.record"
  | "patient.list"
  | "patient.create"
  | "patient.read"
  | "patient.upload"
  | "patient.assign"
  | "patient.remove_link"
  | "formula.read"
  | "formula.draft"
  | "formula.edit"
  | "formula.submit"
  | "formula.acknowledge";

/* A clinic owner runs staff and assignments and sees no clinical record by that role alone (PRA-003). */
export const CLINIC_PERMISSIONS: Readonly<Record<ClinicRole, readonly ClinicPermission[]>> = {
  owner: [
    "member.invite",
    "member.update",
    "member.remove",
    "patient.list",
    "patient.assign",
    "patient.remove_link",
  ],
  practitioner: [
    "licence.record",
    "patient.list",
    "patient.create",
    "patient.read",
    "patient.upload",
    "patient.remove_link",
    "formula.read",
    "formula.draft",
    "formula.edit",
    "formula.submit",
    "formula.acknowledge",
  ],
  assistant: ["patient.list", "patient.create", "patient.read", "patient.upload"],
};

export interface Membership {
  readonly organizationId: string;
  readonly roles: readonly ClinicRole[];
}

export function clinicRolesGrant(
  roles: readonly ClinicRole[],
  permission: ClinicPermission,
): boolean {
  return roles.some((role) => CLINIC_PERMISSIONS[role].includes(permission));
}

/**
 * The clinic a session works in: the one it chose, while the person is still a member, or their
 * only clinic. Null for a member of several who has not chosen, and for someone in no clinic.
 */
export function activeClinicOf(
  memberships: readonly Membership[],
  chosen: string | null,
): string | null {
  if (chosen !== null && memberships.some((m) => m.organizationId === chosen)) return chosen;

  const [only] = memberships;

  return memberships.length === 1 && only !== undefined ? only.organizationId : null;
}

/**
 * A second factor is an authenticator app code, a backup code, or a passkey checked with the
 * device's PIN or biometric. Anyone holding a platform role or a clinic membership must pass one in
 * each session before any route but enrolment answers. Customers need none: their first factor is
 * a code or link sent to their email.
 */
export interface SecondFactor {
  readonly required: boolean;
  /** An authenticator app is confirmed, or a passkey is registered. */
  readonly enrolled: boolean;
  /** This session passed a second factor after it signed in. */
  readonly passed: boolean;
  /** It passed one in the last 15 minutes, which step-up routes need. */
  readonly recent: boolean;
}

/** How long a passed second factor stays fresh enough for a step-up route. */
export const STEP_UP_WINDOW_MS = 15 * 60 * 1000;

export function secondFactorRequired(
  platformRoles: readonly PlatformRole[],
  memberships: readonly Membership[],
): boolean {
  return platformRoles.length > 0 || memberships.length > 0;
}

/** Sent by the "Confirm it's you" dialogs with a passkey for the session they are already in, and read by the Worker. */
export const STEP_UP_HEADER = "x-step-up";

const HOUR_MS = 60 * 60 * 1000;

/**
 * How long a session may live from sign-in, renewals included, or null for Better Auth's own 30
 * sliding days. Staff get 12 hours and clinic members 7 days, so a stolen cookie for a privileged
 * account stops working the same day. The strictest role wins.
 */
export function sessionLifetimeMs(
  platformRoles: readonly PlatformRole[],
  memberships: readonly Membership[],
): number | null {
  if (platformRoles.length > 0) return 12 * HOUR_MS;

  return memberships.length > 0 ? 7 * 24 * HOUR_MS : null;
}

export interface Actor {
  readonly userId: string;
  /** The session this request arrived with. */
  readonly sessionId: string;
  readonly platformRoles: readonly PlatformRole[];
  /** The patient record this user owns, if they are a customer. */
  readonly ownPatientId: string | null;
  readonly memberships: readonly Membership[];
  /** The clinic this session works in, from activeClinicOf. Always one of `memberships`. */
  readonly activeClinicId: string | null;
  /** An account started by an upload, before any email was given. */
  readonly anonymous: boolean;
  readonly emailVerified: boolean;
  readonly banned: boolean;
  readonly secondFactor: SecondFactor;
}

export function rolesGrant(
  roles: readonly PlatformRole[],
  permission: PlatformPermission,
): boolean {
  return roles.some((role) => PLATFORM_PERMISSIONS[role].includes(permission));
}

/** What /api/me answers: who is signed in, what they may do, and where sign-in stands. */
export interface MeView {
  readonly userId: string;
  readonly email: string | null;
  /** The name on the account, or null before they have given one. */
  readonly name: string | null;
  readonly anonymous: boolean;
  /** The person owns a patient record, so the customer account pages have something to show. */
  readonly customer: boolean;
  readonly platformRoles: readonly PlatformRole[];
  readonly permissions: readonly PlatformPermission[];
  readonly memberships: readonly Membership[];
  readonly activeClinicId: string | null;
  readonly secondFactor: SecondFactor;
}

/**
 * Where someone goes after signing in, or after passing their second factor: the admin app for
 * platform staff, the clinic portal for clinic members, otherwise the customer account.
 */
export function homePathFor(me: Pick<MeView, "platformRoles" | "memberships">): string {
  if (me.platformRoles.length > 0) return "/admin/";

  return me.memberships.length > 0 ? "/clinic" : "/account";
}

/** The page that finishes signing in, or null when nothing is left to do. */
export function secondFactorPathFor(me: Pick<MeView, "secondFactor">): string | null {
  const { required, enrolled, passed } = me.secondFactor;

  if (!required || passed) return null;

  return enrolled ? "/security/verify" : "/security/setup";
}

export function hasPlatformPermission(actor: Actor, permission: PlatformPermission): boolean {
  return rolesGrant(actor.platformRoles, permission);
}

/** Every platform permission the actor holds, for clients that show or hide what a person can use. */
export function platformPermissionsOf(actor: Actor): readonly PlatformPermission[] {
  return PLATFORM_PERMISSIONS_LIST.filter((permission) => hasPlatformPermission(actor, permission));
}

/**
 * Whether the actor may lock or unlock this account. Support locks customers and clinic people only,
 * so a stolen support login cannot lock out the staff who would revoke it. Locking staff takes
 * someone who grants roles, and only a super admin locks an admin or another super admin.
 */
export function mayLock(
  actorRoles: readonly PlatformRole[],
  targetRoles: readonly PlatformRole[],
): boolean {
  if (!rolesGrant(actorRoles, "user.ban")) return false;

  if (targetRoles.length === 0) return true;

  if (!rolesGrant(actorRoles, "user.set_role")) return false;

  const senior = targetRoles.some((role) => role === "super_admin" || role === "admin");

  return !senior || actorRoles.includes("super_admin");
}

/**
 * The platform roles this actor may grant or take away. A super admin grants every role. An admin
 * grants the roles below admin, so an admin cannot make another admin. Anyone else grants none.
 */
export function grantableRoles(actorRoles: readonly PlatformRole[]): readonly PlatformRole[] {
  if (!rolesGrant(actorRoles, "user.set_role")) return [];

  if (actorRoles.includes("super_admin")) return PLATFORM_ROLES;

  return PLATFORM_ROLES.filter((role) => role !== "super_admin" && role !== "admin");
}

/**
 * Whether the actor may change `target`'s roles to `next`. Every role added or removed must be one
 * the actor grants, so an admin can neither promote someone to admin nor demote an admin.
 */
export function mayChangeRoles(
  actorRoles: readonly PlatformRole[],
  current: readonly PlatformRole[],
  next: readonly PlatformRole[],
): boolean {
  const grantable = grantableRoles(actorRoles);
  const changed = PLATFORM_ROLES.filter((role) => current.includes(role) !== next.includes(role));

  return grantable.length > 0 && changed.every((role) => grantable.includes(role));
}

/** The roles the actor holds in the session's active clinic, empty when there is none. */
export function activeClinicRoles(actor: Actor): readonly ClinicRole[] {
  return actor.memberships.find((m) => m.organizationId === actor.activeClinicId)?.roles ?? [];
}

/** The active clinic's id when the actor's roles there grant this permission, otherwise null. */
export function activeClinicGranting(actor: Actor, permission: ClinicPermission): string | null {
  return clinicRolesGrant(activeClinicRoles(actor), permission) ? actor.activeClinicId : null;
}

/**
 * What a route needs before its handler runs.
 *
 *   public            no session
 *   session           any session, an anonymous one started by an upload included
 *   verified          a session for an account with a verified email, never an anonymous one
 *   customer          owns a patient record, anonymous included
 *   verifiedCustomer  owns a patient record and signed in with a verified email
 *   record            any session. The handler decides with canReadPatient, because the caller may
 *                     be the patient, their clinic or staff
 *   platform          one platform permission
 *   clinic            one clinic permission, held in the session's active clinic
 */
export type RouteAccess =
  | NamedAccess
  | { readonly platform: PlatformPermission }
  | { readonly clinic: ClinicPermission };

const NAMED_ACCESS = [
  "public",
  "session",
  "verified",
  "customer",
  "verifiedCustomer",
  "record",
] as const;

type NamedAccess = (typeof NAMED_ACCESS)[number];

function isNamedAccess(access: RouteAccess): access is NamedAccess {
  return NAMED_ACCESS.some((name) => name === access);
}

/** The platform permission a route declares, or null when it declares another kind of access. */
export function platformPermissionOf(access: RouteAccess): PlatformPermission | null {
  if (isNamedAccess(access) || !("platform" in access)) return null;

  return access.platform;
}

export type Authorization =
  | { readonly allowed: true }
  | { readonly allowed: false; readonly status: 401 | 403 | 409; readonly reason: string };

const ALLOWED: Authorization = { allowed: true };

function refused(status: 401 | 403 | 409, reason: string): Authorization {
  return { allowed: false, status, reason };
}

function isVerified(actor: Actor): boolean {
  return !actor.anonymous && actor.emailVerified;
}

function customerAccess(actor: Actor, verified: boolean): Authorization {
  if (actor.ownPatientId === null) return refused(403, "forbidden");

  if (verified && !isVerified(actor)) return refused(403, "verified_account_required");

  return ALLOWED;
}

/**
 * The refusal for a staff or clinic session that has not passed its second factor, or null. Session
 * routes stay open (who am I, enrolment, verification) so the person can finish signing in.
 * Everything else waits, the person's own customer routes included, so a privileged account never
 * runs half signed in.
 */
function heldForSecondFactor(actor: Actor, access: RouteAccess): Authorization | null {
  const { required, enrolled, passed } = actor.secondFactor;

  if (!required || passed || access === "session") return null;

  return refused(403, enrolled ? "second_factor_required" : "second_factor_enrolment_required");
}

/* A member of several clinics who has not chosen one gets 409, so the portal can ask which. */
function clinicAccess(actor: Actor, permission: ClinicPermission): Authorization {
  if (actor.memberships.length > 0 && actor.activeClinicId === null)
    return refused(409, "choose_clinic");

  return activeClinicGranting(actor, permission) === null ? refused(403, "forbidden") : ALLOWED;
}

/**
 * `stepUp` is the route's flag. A step-up route answers 403 step_up_required to a staff or clinic
 * session whose second factor is older than STEP_UP_WINDOW_MS, after every other check passed, so
 * the client knows a fresh code will be enough.
 */
export function authorize(actor: Actor | null, access: RouteAccess, stepUp = false): Authorization {
  const decision = accessDecision(actor, access);

  if (!decision.allowed || !stepUp || actor === null) return decision;
  const { required, recent } = actor.secondFactor;

  return required && !recent ? refused(403, "step_up_required") : decision;
}

function accessDecision(actor: Actor | null, access: RouteAccess): Authorization {
  if (access === "public") return ALLOWED;

  if (actor === null) return refused(401, "signed_out");

  if (actor.banned) return refused(403, "banned");

  const held = heldForSecondFactor(actor, access);

  if (held !== null) return held;

  if (access === "session" || access === "record") return ALLOWED;

  if (access === "verified")
    return isVerified(actor) ? ALLOWED : refused(403, "verified_account_required");

  if (access === "customer") return customerAccess(actor, false);

  if (access === "verifiedCustomer") return customerAccess(actor, true);

  if ("platform" in access)
    return hasPlatformPermission(actor, access.platform) ? ALLOWED : refused(403, "forbidden");

  return clinicAccess(actor, access.clinic);
}

export interface ClinicLink {
  readonly organizationId: string;
  readonly status: "pending" | "active" | "revoked";
}

export interface ReadRequest {
  readonly patientId: string;
  readonly links: readonly ClinicLink[];
  /** True when the case is in this staff member's review queue. */
  readonly inQueue: boolean;
  /** A staff member's stated reason for opening the record outside their queue. */
  readonly reason: string | null;
}

export type Access =
  | { readonly allowed: true; readonly via: "self" | "clinic" | "staff"; readonly audit: boolean }
  | { readonly allowed: false; readonly why: "banned" | "no_relationship" | "staff_needs_reason" };

function staffAccess(actor: Actor, request: ReadRequest): Access | null {
  if (!hasPlatformPermission(actor, "patient.read_record")) return null;

  if (request.inQueue || (request.reason ?? "").trim().length > 0)
    return { allowed: true, via: "staff", audit: true };

  return { allowed: false, why: "staff_needs_reason" };
}

/**
 * Three ways in, and only three:
 *   self    the signed-in user owns the patient record
 *   clinic  the user may read patients in the session's active clinic, and that clinic has an
 *           active link to the patient. What they see of the record is the link's share scope
 *           (PRA-030): clinicSeesDocument.
 *   staff   a platform role with patient.read_record, with the case in their queue or a stated
 *           reason (ADM-004). Staff reads are always audited (PRI-030).
 */
export function canReadPatient(actor: Actor, request: ReadRequest): Access {
  if (actor.banned) return { allowed: false, why: "banned" };

  if (actor.ownPatientId === request.patientId) return { allowed: true, via: "self", audit: false };

  const clinic = activeClinicGranting(actor, "patient.read");

  if (request.links.some((l) => l.status === "active" && l.organizationId === clinic))
    return { allowed: true, via: "clinic", audit: false };

  return staffAccess(actor, request) ?? { allowed: false, why: "no_relationship" };
}
